import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, unref as _unref, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-07a5c172"),n=n(),_popScopeId(),n)
const _hoisted_1 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "icon is-small" }, [
  /*#__PURE__*/_createElementVNode("i", { class: "fas fa-chevron-left" })
], -1))
const _hoisted_2 = [
  _hoisted_1
]
const _hoisted_3 = ["title"]
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("i", { class: "fas fa-info-circle" }, null, -1))
const _hoisted_5 = [
  _hoisted_4
]
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "icon is-small" }, [
  /*#__PURE__*/_createElementVNode("i", { class: "fas fa-chevron-right" })
], -1))
const _hoisted_7 = [
  _hoisted_6
]
const _hoisted_8 = { style: { 'font-size': '1rem', 'font-weight': 'normal' } }

import BaseModal from '@/components/common/modal/BaseModal.vue'
import i18n from '@/locales'
import { AnnouncementLibraryResponse } from '@/interfaces/library/announcement'
import { AnnouncementType } from '@/interfaces/admin/announcement'
import { computed, onMounted, onUnmounted, PropType, ref, watchEffect } from 'vue'
import { customDownload } from '@/helpers/customDownload'
import { getMediaById } from '@/controllers/library'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useWidgetNavigator } from '@/components/widgets/composables/useWidgetNavigator'
import { WidgetRootBreadcrumbs } from '@/interfaces/global'


export default /*#__PURE__*/_defineComponent({
  __name: 'AnnouncementBannerContent',
  props: {
  announcements: {
    type: Array as PropType<AnnouncementLibraryResponse[]>,
    required: true,
  },
},
  setup(__props) {

const props = __props;

const store = useStore();
const route = useRoute();
const router = useRouter();
const { navToViewWithLoading, setRootBreadcrumb } = useWidgetNavigator()
const currentIndex = ref(0);
const isPaused = ref(false); // Track hover state
const isWidget: boolean = store.getters['widgets/isWidget'];
let intervalId: number | undefined;

const libraryExceptions = [
  'Exporter',
  'JobManager',
  'Notification', // News Items
  'Notifications', // News Items
  'Order',
  'Orders',
  'OrderSubmitted',
  'Preferences', // Settings
  'PurchaseOrder',
  'Profile', // Settings
  'ReportsManager', // Data Insights
  'RequestForQuote'
];
const isLibraryPage = computed(() => {
  const currentRouteName = typeof route.name === 'string' ? route.name : '';
  const isLibrary = !route.path.startsWith('/admin');
  return isLibrary && !libraryExceptions.includes(currentRouteName);
});

const modalSubmitLabel = computed(() => {
  switch (currentAnnouncement.value?.type) {
    case AnnouncementType.FILE:
      return i18n.global.t('download')
    default:
      return i18n.global.t('open')
  }
})
const showNavigation = computed(() => (props.announcements?.length || 0) > 1);
const currentAnnouncement = computed<AnnouncementLibraryResponse | undefined>(() => {
  if (props.announcements && props.announcements.length > 0) {
    return props.announcements[currentIndex.value];
  }
  return undefined;
});

const defaultBannerColor = store.getters['theme/bannersColor'];
const defaultBannerColorInverted = store.getters['theme/bannersColorInverted'];
const currentBannerStyle = computed(() => {
  if (currentAnnouncement.value === undefined) {
    return {
      backgroundColor: defaultBannerColor,
      color: defaultBannerColorInverted
    }
  }

  return {
    backgroundColor: currentAnnouncement.value.barColor ?? defaultBannerColor,
    color: currentAnnouncement.value.textColor ?? defaultBannerColorInverted
  }
})

const titleSpanRef = ref(null);
const isTitleTruncated = ref(false);

// --- Viewport Detection ---
const checkViewport = () => {
  checkTitleTruncation(); // ensures reactivity when resizing window
  isAnnouncementModalOpen.value = false;
};

// --- Truncation + Viewport Condition ---
const shouldShowInfoIcon = computed(() => isTitleTruncated.value);

// --- Function to check truncation (called by transition hook) ---
const checkTitleTruncation = () => {
  if (titleSpanRef.value) {
    isTitleTruncated.value = titleSpanRef.value.scrollWidth > titleSpanRef.value.clientWidth;
  } else {
    isTitleTruncated.value = false;
  }
};

// --- Modal State & Handlers ---
const isAnnouncementModalOpen = ref(false);

const showModal = () => {
  pauseRotation();
  isAnnouncementModalOpen.value = true;
};

const closeModal = () => {
  resumeRotation();
  isAnnouncementModalOpen.value = false;
};

const stopRotation = () => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = undefined;
  }
};

const startRotation = () => {
  stopRotation(); // Clear any existing interval before starting
  // Start new interval only if there are multiple announcements and not paused
  if (showNavigation.value && !isPaused.value && !isAnnouncementModalOpen.value) {
    intervalId = setInterval(() => {
      currentIndex.value = (currentIndex.value + 1) % props.announcements.length;
    }, 8500); // Rotate every 8.5 seconds
  }
};

const resetTimer = () => {
  isTitleTruncated.value = false;
  startRotation(); // Simply restart the rotation, which includes clearing the old timer
};

const nextAnnouncement = () => {
  if (!props.announcements || props.announcements.length === 0) return;
  currentIndex.value = (currentIndex.value + 1) % props.announcements.length;
  resetTimer(); // Reset timer on manual navigation
};

const prevAnnouncement = () => {
  if (!props.announcements || props.announcements.length === 0) return;
  currentIndex.value = (currentIndex.value - 1 + props.announcements.length) % props.announcements.length;
  resetTimer(); // Reset timer on manual navigation
};

const pauseRotation = () => {
  isPaused.value = true;
  stopRotation();
};

const resumeRotation = () => {
  isPaused.value = false;
  startRotation();
};

const isClickable = (announcement: AnnouncementLibraryResponse): boolean => {
  return !!announcement.externalUrl || !!announcement.relativePath || !!announcement.targetMedia;
};

const handleAnnouncementClick = async (announcement: AnnouncementLibraryResponse) => {
  isAnnouncementModalOpen.value = false;

  switch (announcement.type) {
    case AnnouncementType.EXTERNAL:
      if (announcement.externalUrl) {
        window.open(announcement.externalUrl, '_blank', 'noopener,noreferrer');
      }
      break;
    case AnnouncementType.FILE:
      if (announcement.relativePath) {
        // Check if the path ends with '.pdf' (case-insensitive)
        if (announcement.relativePath.toLowerCase().endsWith('.pdf')) {
          // Open PDFs in a new tab
          window.open(announcement.relativePath, '_blank', 'noopener,noreferrer');
        } else {
          // For other file types, use custom download
          customDownload(announcement.relativePath);
        }
      }
      break;
    case AnnouncementType.MEDIA:
      if (announcement.targetMedia?.identifier) {
        switch (isWidget) {
          case true: {
            const mediaData = await getMediaById(announcement.targetMedia.id);
            setRootBreadcrumb(WidgetRootBreadcrumbs.search);
            await navToViewWithLoading(mediaData);
            break;
          }
          case false: {
            await router.push({
              name: 'MediaIdentifierRedirect',
              params: { id: announcement.targetMedia.identifier }
            });
            break;
          }
        }
      }
  }
  // If neither URL nor path is present, clicking does nothing
};

onMounted(async () => {
  startRotation();
  window.addEventListener('resize', checkViewport);
  checkViewport();
});

onUnmounted(() => {
  window.removeEventListener('resize', checkViewport);
  stopRotation();
});

watchEffect(() => {
  if (currentAnnouncement.value && titleSpanRef.value) {
    checkTitleTruncation();
  } else {
    isTitleTruncated.value = false;
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _withDirectives(_createElementVNode("div", {
      class: _normalizeClass(["announcement-banner-container", { 'is-centered': !showNavigation.value }]),
      style: _normalizeStyle(currentBannerStyle.value),
      onMouseenter: pauseRotation,
      onMouseleave: resumeRotation
    }, [
      _createVNode(_Transition, {
        name: "fade",
        mode: "out-in"
      }, {
        default: _withCtx(() => [
          _withDirectives(_createElementVNode("button", {
            class: "nav-button prev",
            onClick: prevAnnouncement
          }, _hoisted_2, 512), [
            [_vShow, showNavigation.value]
          ])
        ], undefined, true),
        _: 1
      }),
      (!!currentAnnouncement.value)
        ? (_openBlock(), _createBlock(_Transition, {
            key: 0,
            name: "fade",
            mode: "out-in"
          }, {
            default: _withCtx(() => [
              (_openBlock(), _createElementBlock("div", {
                class: _normalizeClass(["announcement-item mx-2", { 'is-clickable': isClickable(currentAnnouncement.value) }]),
                key: currentAnnouncement.value.id,
                onClick: _cache[1] || (_cache[1] = ($event: any) => (handleAnnouncementClick(currentAnnouncement.value)))
              }, [
                _createElementVNode("span", {
                  class: "announcement-title has-text-weight-bold",
                  ref_key: "titleSpanRef",
                  ref: titleSpanRef,
                  title: currentAnnouncement.value.title
                }, _toDisplayString(currentAnnouncement.value.title), 9, _hoisted_3),
                _withDirectives(_createElementVNode("span", {
                  class: "icon is-small mx-2",
                  onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (showModal()), ["stop"]))
                }, _hoisted_5, 512), [
                  [_vShow, shouldShowInfoIcon.value]
                ])
              ], 2))
            ], undefined, true),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createVNode(_Transition, {
        name: "fade",
        mode: "out-in"
      }, {
        default: _withCtx(() => [
          _withDirectives(_createElementVNode("button", {
            class: "nav-button next",
            onClick: nextAnnouncement
          }, _hoisted_7, 512), [
            [_vShow, showNavigation.value]
          ])
        ], undefined, true),
        _: 1
      })
    ], 38), [
      [_vShow, props.announcements?.length > 0 && isLibraryPage.value]
    ]),
    (isAnnouncementModalOpen.value)
      ? (_openBlock(), _createBlock(BaseModal, {
          key: 0,
          onClose: closeModal
        }, {
          title: _withCtx(() => [
            _createElementVNode("span", _hoisted_8, _toDisplayString(currentAnnouncement.value?.title), 1)
          ]),
          actions: _withCtx(() => [
            _createElementVNode("button", {
              class: "button",
              onClick: closeModal
            }, [
              _createElementVNode("span", null, _toDisplayString(_unref(i18n).global.t('close')), 1)
            ]),
            (currentAnnouncement.value ? isClickable(currentAnnouncement.value) : false)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  class: "button is-primary",
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (handleAnnouncementClick(currentAnnouncement.value!!)))
                }, [
                  _createElementVNode("span", null, _toDisplayString(modalSubmitLabel.value), 1)
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})